import React, { useContext, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Edit, Trash2, Plus, Search, ChevronLeft, ChevronRight, Download, Package, DollarSign, TrendingUp, ArrowUp, ArrowDown } from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useNotification } from '../../contexts/NotificationContext';
import { AuthContext } from '../../contexts/AuthContext';
import { useInventoryList } from './shared/services/useInventoryService';

const ROWS_PER_PAGE = 5;

const InventoryListPage = () => {
  const { userRole, permissions } = useContext(AuthContext);
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '0', 10);
  const { items, loading, deleteItem } = useInventoryList();
  const [sortConfig, setSortConfig] = useState({ key: 'product_name', direction: 'asc' });

  const hasPermission = (perm) => (userRole || '').toLowerCase() === 'admin' || (permissions || []).includes(perm);
  const showActionsColumn = hasPermission('Inventory Edit') || hasPermission('Inventory Delete');

  const filtered = items.filter(item =>
    (item.product_name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Apply Sorting
  filtered.sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aVal = a[sortConfig.key] || '';
    const bVal = b[sortConfig.key] || '';
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const paginated = filtered.slice(currentPage * ROWS_PER_PAGE, (currentPage + 1) * ROWS_PER_PAGE);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  // Stats calculation
  const totalItemsCount = items.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0);
  const totalValue = items.reduce((sum, item) => sum + (parseFloat(item.price) * parseInt(item.quantity) || 0), 0);

  const handleExportPDF = async () => {
    const doc = new jsPDF();
    const logoUrl = '/logo.png';

    const toDataURL = (url) => new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = url;
    });

    try {
      const logoData = await toDataURL(logoUrl);
      doc.addImage(logoData, 'PNG', 14, 10, 30, 30);
    } catch (error) {
      console.error('Failed to load logo image', error);
    }

    doc.setFontSize(18);
    doc.text('Inventory Report', 50, 25);
    
    const headers = ['S/No', 'Product Name', 'Quantity', 'Price', 'Total Value'];
    const data = filtered.map((row, index) => [
      index + 1,
      row.product_name,
      row.quantity,
      `$${parseFloat(row.price).toFixed(2)}`,
      `$${(parseFloat(row.price) * parseInt(row.quantity)).toFixed(2)}`
    ]);

    autoTable(doc, {
      startY: 45,
      head: [headers],
      body: data,
      headStyles: { fillColor: [0, 128, 128], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [240, 240, 240] },
    });

    doc.save('inventory_report.pdf');
  };

  const handleDelete = (item) => {
    showNotification('confirm', `Delete item "${item.product_name}"?`, async () => {
      try {
        await deleteItem(item.id);
        showNotification('success', 'Item deleted successfully');
      } catch (err) {
        showNotification('error', 'Failed to delete item');
      }
    });
  };

  if (loading) return (
    <div className="w-full max-w-6xl mx-auto p-4 animate-pulse space-y-4">
      <div className="grid grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>)}
      </div>
      <div className="h-12 bg-gray-200 rounded"></div>
      {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-gray-200 rounded"></div>)}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6 space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={<Package className="text-blue-600" />} label="Total Items" value={totalItemsCount.toLocaleString()} bgColor="bg-blue-100" />
        <StatCard icon={<DollarSign className="text-green-600" />} label="Total Value" value={`$${totalValue.toFixed(2)}`} bgColor="bg-green-100" />
        <StatCard icon={<TrendingUp className="text-purple-600" />} label="Product Types" value={items.length} bgColor="bg-purple-100" />
      </div>

      {/* Action bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="relative w-full lg:w-2/3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search products by name..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setSearchParams({ page: '0' }); }}
              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none transition-all"
            />
          </div>
          <div className="flex gap-3 w-full lg:w-auto">
            {hasPermission('Inventory Add') && (
              <button 
                onClick={() => navigate('/addInventory')} 
                className="flex items-center gap-2 bg-teal-600 text-white px-4 py-3 rounded-lg hover:bg-teal-700 font-medium flex-1 lg:flex-none justify-center"
              >
                <Plus className="h-4 w-4" /> Add Product
              </button>
            )}
            <button onClick={handleExportPDF} className="flex items-center gap-2 bg-gray-600 text-white px-4 py-3 rounded-lg hover:bg-gray-700 font-medium flex-1 lg:flex-none justify-center">
              <Download className="h-4 w-4" /> Export PDF
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-teal-50 border-b border-teal-100">
              <th className="text-left p-4 font-semibold text-teal-900">S/No</th>
              {[
                { label: 'Product Name', key: 'product_name' },
                { label: 'Quantity', key: 'quantity' },
                { label: 'Price', key: 'price' },
                { label: 'Total Value', key: 'total_value' }
              ].map(h => (
                <th 
                  key={h.key} 
                  onClick={() => h.key !== 'total_value' && requestSort(h.key)}
                  className={`text-left p-4 font-semibold text-teal-900 ${h.key !== 'total_value' ? 'cursor-pointer hover:bg-teal-100/50' : ''} transition-colors`}
                >
                  <div className="flex items-center">
                    {h.label}
                    {sortConfig.key === h.key && (
                      sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />
                    )}
                  </div>
                </th>
              ))}
              {showActionsColumn && <th className="text-left p-4 font-semibold text-teal-900">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr><td colSpan={showActionsColumn ? 6 : 5} className="p-12 text-center text-gray-500">No products found</td></tr>
            ) : paginated.map((item, i) => (
              <tr key={item.id} className={`border-b border-gray-100 hover:bg-teal-50/50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                <td className="p-4">{currentPage * ROWS_PER_PAGE + i + 1}</td>
                <td className="p-4 font-medium">{item.product_name}</td>
                <td className="p-4">
                  <span className={`px-2 py-1 rounded text-sm font-medium ${item.quantity < 50 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {item.quantity}
                  </span>
                </td>
                <td className="p-4">${parseFloat(item.price).toFixed(2)}</td>
                <td className="p-4 font-medium">${(parseFloat(item.price) * parseInt(item.quantity)).toFixed(2)}</td>
                {showActionsColumn && (
                  <td className="p-4">
                    <div className="flex gap-2">
                      {hasPermission('Inventory Edit') && (
                        <button 
                          onClick={() => navigate(`/editInventory/${item.id}`, { 
                            state: { activeMenu: 'Inventory', page: currentPage } 
                          })} 
                          className="flex items-center gap-2 px-3 py-2 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors font-medium text-sm"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                          Edit
                        </button>
                      )}
                      {hasPermission('Inventory Delete') && (
                        <button 
                          onClick={() => handleDelete(item)} 
                          className="flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors font-medium text-sm"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {paginated.map((item, i) => (
          <div key={item.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 text-lg mb-1">{item.product_name}</h3>
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm text-gray-500">#{currentPage * ROWS_PER_PAGE + i + 1}</span>
              <span className={`px-2 py-1 rounded text-sm font-medium ${item.quantity < 50 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{item.quantity} units</span>
            </div>
            <div className="flex justify-between text-sm mb-4">
              <span className="text-gray-600">Total Value:</span>
              <span className="font-bold text-gray-900">${(parseFloat(item.price) * parseInt(item.quantity)).toFixed(2)}</span>
            </div>
            <div className="flex gap-2 pt-3 border-t border-gray-100 flex-wrap">
              {hasPermission('Inventory Edit') && (
                <button 
                  onClick={() => navigate(`/editInventory/${item.id}`, { 
                    state: { activeMenu: 'Inventory', page: currentPage } 
                  })} 
                  className="flex items-center justify-center gap-2 px-3 py-2 text-blue-600 border border-blue-200 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors"
                >
                  <Edit className="h-4 w-4" />
                  Edit
                </button>
              )}
              {hasPermission('Inventory Delete') && (
                <button 
                  onClick={() => handleDelete(item)} 
                  className="flex items-center justify-center gap-2 px-3 py-2 text-red-600 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {filtered.length > 0 && totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center justify-between">
          <div className="text-sm text-gray-600">Showing {currentPage * ROWS_PER_PAGE + 1}–{Math.min((currentPage + 1) * ROWS_PER_PAGE, filtered.length)} of {filtered.length}</div>
          <div className="flex gap-2">
            <button onClick={() => setSearchParams({ page: '0' })} disabled={currentPage === 0} className="p-2 border rounded-lg disabled:opacity-50 text-xs font-bold uppercase tracking-widest">First</button>
            <button onClick={() => setSearchParams({ page: Math.max(0, currentPage - 1).toString() })} disabled={currentPage === 0} className="p-2 border rounded-lg disabled:opacity-50"><ChevronLeft className="h-4 w-4" /></button>
            <span className="px-4 py-2 bg-gray-50 rounded-lg text-sm font-medium">{currentPage + 1} of {totalPages}</span>
            <button onClick={() => setSearchParams({ page: Math.min(totalPages - 1, currentPage + 1).toString() })} disabled={currentPage >= totalPages - 1} className="p-2 border rounded-lg disabled:opacity-50"><ChevronRight className="h-4 w-4" /></button>
            <button onClick={() => setSearchParams({ page: (totalPages - 1).toString() })} disabled={currentPage >= totalPages - 1} className="p-2 border rounded-lg disabled:opacity-50 text-xs font-bold uppercase tracking-widest">Last</button>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon, label, value, bgColor }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
    <div className="flex items-center gap-4">
      <div className={`p-3 ${bgColor} rounded-xl`}>{icon}</div>
      <div>
        <p className="text-sm text-gray-600 font-medium">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  </div>
);

export default InventoryListPage;
